import type { StyleRule } from '@archipro-design/aria';
import { pxToRem } from '@archipro-design/aria';
import {
    DUO_COLUMN_WIDTH,
    ROW_GAP_MOBILE,
} from '@modules/page-builder/config/column-constants';

export const DuoImageModuleContainer: StyleRule = ({ theme }) => ({
    display: 'flex',
    flexFlow: 'column',

    [theme.screen.laptop]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        width: '100%',
        columnGap: pxToRem(40),
        gridTemplateRows: `1fr`,
        maxWidth: pxToRem(DUO_COLUMN_WIDTH + 100),
        margin: '0 auto',
    },
});

export const DuoImageContainerImage: (
    isMedium: boolean,
    isSmall?: boolean,
    isPortrait?: boolean
) => StyleRule<{
    columns: number;
    mobile: boolean;
}> =
    (isMedium, isSmall, isPortrait) =>
    ({ columns, mobile, theme }) => {
        if (isSmall) {
            // Retain original small image aspect ratio but bump width to 500
            return {
                margin: 'auto',
                width: pxToRem(500),
                height: `calc(${pxToRem(500)} * 600 / 400) !important`,
                '& > div': {
                    height: `calc(${pxToRem(500)} * 600 / 400) !important`,
                    minHeight: `calc(${pxToRem(500)} * 600 / 400) !important`,
                    maxHeight: 'none !important',
                },
            };
        }

        if (!isPortrait) {
            // Retain original landscape aspect ratio but bump width to 750
            return {
                margin: '0',
                width: pxToRem(750),
                height: `calc(${pxToRem(750)} * 410 / 615) !important`,
                '& > div': {
                    height: `calc(${pxToRem(750)} * 410 / 615) !important`,
                    minHeight: `calc(${pxToRem(750)} * 410 / 615) !important`,
                    maxHeight: 'none !important',
                },
            };
        }

        // Now only apply this style to portrait / medium non mobile images.
        if (isPortrait || (isMedium && !mobile)) {
            return {
                margin: '0',
                width: pxToRem(750),
                height: `${pxToRem(1125)} !important`,
                display: 'flex',
                flexDirection: 'column',
                // Override height constraints only for the image container
                '& > div': {
                    height: `${pxToRem(1125)} !important`,
                    minHeight: `${pxToRem(1125)} !important`,
                    maxHeight: 'none !important',
                },
            };
        }

        if (mobile) {
            return Object.assign({
                margin: '0 auto',
                '&:last-child':
                    columns > 1
                        ? {
                              margin: `${pxToRem(ROW_GAP_MOBILE)} auto 0`,
                          }
                        : undefined,
            });
        }
        return {
            margin: `${pxToRem(ROW_GAP_MOBILE)} auto 0`,
            [theme.screen.laptop]: {
                margin: 'auto',
                '&:has(.isMedium)': {
                    margin: '0 auto',
                },
            },
        };
    };

export const DuoImageModuleContainerPageBuilder: StyleRule = ({ theme }) => ({
    display: 'flex',
    flexFlow: 'column',

    [theme.screen.laptop]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        columnGap: pxToRem(204),
        gridTemplateRows: `1fr`,
        maxWidth: pxToRem(DUO_COLUMN_WIDTH),
        margin: 'auto',
    },
});

export const DuoImageContainerImagePageBuilder: (
    isMedium: boolean
) => StyleRule<{
    columns: number;
    mobile: boolean;
}> =
    (isMedium) =>
    ({ columns, mobile, theme }) => {
        if (isMedium && !mobile) {
            return { margin: '0 auto' };
        }

        if (mobile) {
            return Object.assign({
                margin: '0 auto',
                '&:last-child':
                    columns > 1
                        ? {
                              margin: `${pxToRem(ROW_GAP_MOBILE)} auto 0`,
                          }
                        : undefined,
            });
        }
        return {
            margin: `${pxToRem(ROW_GAP_MOBILE)} auto 0`,
            [theme.screen.laptop]: {
                margin: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100%',
                '&:has(.isMedium)': {
                    margin: '0 auto',
                },
            },
        };
    };
